import { useEffect, useState } from "react"
import type { FC } from "react"
import { useParams, useSearchParams } from "react-router-dom"

import { useQuery } from "@apollo/client"
import * as Tabs from "@radix-ui/react-tabs"

import styles from "./CompanyDetailPage.module.scss"

import CompanyBreadcrumb from "@/souhl_admin/components/features/companies/Breadcrumb/CompanyBreadcrumb"
import CompanyTabBar, {
	DEFAULT_TAB,
	VALID_TAB_IDS,
} from "@/souhl_admin/components/features/companies/CompanyTabBar/CompanyTabBar"
import DatasourceConfigTab from "@/souhl_admin/components/features/companies/DatasourceConfigTab/DatasourceConfigTab"
import FeatureFlagTab from "@/souhl_admin/components/features/companies/FeatureFlagTab/FeatureFlagTab"
import FeeRateTab from "@/souhl_admin/components/features/companies/FeeRateTab/FeeRateTab"
import GeneralInfoTab from "@/souhl_admin/components/features/companies/GeneralInfoTab/GeneralInfoTab"
import SalonSelectAndHome from "@/souhl_admin/components/features/companies/SalonSelectAndHome/SalonSelectAndHome"
import UsageStartDateTabContainer from "@/souhl_admin/components/features/companies/UsageStartDateTab/UsageStartDateTabContainer"
import CompanySmsSendingRuleMasterTab from "@/souhl_admin/components/features/companies/sms-sending-rule/CompanySmsSendingRuleMasterTab/CompanySmsSendingRuleMasterTab"
import SmsSendingRuleMappingTab from "@/souhl_admin/components/features/companies/sms-sending-rule/SmsSendingRuleMappingTab/SmsSendingRuleMappingTab"
import SmsSendingStartSettingsTabContainer from "@/souhl_admin/components/features/companies/sms-sending-rule/SmsSendingStartSettingsTab/SmsSendingStartSettingsTabContainer"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

const TAB_COMPONENTS: Record<string, FC<{ companyId: string }>> = {
	general: GeneralInfoTab,
	"sending-rule-master": CompanySmsSendingRuleMasterTab,
	"sending-rule-mapping": SmsSendingRuleMappingTab,
	"sending-start-settings": SmsSendingStartSettingsTabContainer,
	"usage-start-date": UsageStartDateTabContainer,
	"datasource-config": DatasourceConfigTab,
	"feature-flag": FeatureFlagTab,
	"fee-rate": FeeRateTab,
}

const GetCompanyName = graphql(/* GraphQL */ `
	query GetCompanyName($param: CompanyParam!) {
		company(param: $param) {
			company {
				id
				name
			}
		}
	}
`)

const CompanyDetailPage: FC = () => {
	const { companyId } = useParams<{ companyId: string }>()
	const [searchParams, setSearchParams] = useSearchParams()

	// タブ状態管理: URL クエリパラメータ ?tab=xxx で管理
	const tabParam = searchParams.get("tab")
	const activeTab =
		tabParam && VALID_TAB_IDS.has(tabParam) ? tabParam : DEFAULT_TAB

	const handleTabChange = (tabId: string) => {
		setSearchParams({ tab: tabId }, { replace: true })
	}

	// Keep-Alive 方式: 一度でもアクティブになったタブを記録
	const [mountedTabs, setMountedTabs] = useState<Set<string>>(
		new Set([activeTab])
	)

	useEffect(() => {
		setMountedTabs((prev) => new Set(prev).add(activeTab))
	}, [activeTab])

	// companyId 変更時に mountedTabs をリセット（強制リマウント）
	useEffect(() => {
		setMountedTabs(new Set([activeTab]))
	}, [companyId])

	// 会社名取得（パンくず用）
	const { data: companyNameData, loading: companyNameLoading } = useQuery(
		GetCompanyName,
		{
			variables: {
				param: {
					id: companyId ?? "",
				},
			},
			skip: !companyId,
			fetchPolicy: "cache-first",
		}
	)

	if (!companyId) {
		return (
			<div className={styles.container}>
				<div className={styles.error}>会社IDが指定されていません</div>
			</div>
		)
	}

	const companyName = companyNameData?.company.company?.name ?? ""

	if (companyNameLoading && !companyNameData) {
		return (
			<div className={styles.loadingContainer}>
				<div className={styles.loading}>読み込み中...</div>
			</div>
		)
	}

	return (
		<div className={styles.container}>
			<CompanyBreadcrumb companyName={companyName} companyId={companyId} />
			<SalonSelectAndHome companyId={companyId} />

			<Tabs.Root value={activeTab} onValueChange={handleTabChange}>
				<CompanyTabBar activeTab={activeTab} companyId={companyId} />

				{/* Tab Contents: key={companyId} で会社切り替え時に強制リマウント */}
				<div className={styles.tabContent} key={companyId}>
					{Object.entries(TAB_COMPONENTS).map(([tabId, TabComponent]) =>
						mountedTabs.has(tabId) ? (
							<div
								key={tabId}
								style={{
									display: activeTab === tabId ? "block" : "none",
								}}
							>
								<TabComponent companyId={companyId} />
							</div>
						) : null
					)}
				</div>
			</Tabs.Root>
		</div>
	)
}

export default CompanyDetailPage
